import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Star,
  User,
  ChevronLeft,
  ChevronRight,
  X,
  FileDown,
  FileUp,
  Download,
  Database,
} from 'lucide-react';

interface RecipeFilters {
  searchQuery: string;
  category: string;
  showFavoritesOnly: boolean;
  showOwnRecipesOnly: boolean;
}

interface PaginationState {
  limit: number;
  totalCount: number;
  offset: number;
  currentPage: number;
}

interface RecipeFilterToolbarProps {
  filters: RecipeFilters;
  pagination: PaginationState;
  loading?: boolean;
  selectedCount?: number;
  onSearchChange: (query: string) => void;
  onCategoryChange: (category: string) => void;
  onFavoritesToggle: (show: boolean) => void;
  onShowOwnRecipesOnlyToggle: (show: boolean) => void;
  onLimitChange: (limit: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onExportSelected?: () => void;
  onExportAll?: () => void;
  onImport?: () => void;
  isExporting?: boolean;
  showActions?: boolean;
  showStats?: boolean;
  variant?: 'home' | 'manage';
}

const CATEGORIES = [
  'Asian',
  'Sri Lankan',
  'Mexican',
  'Latin American',
  'Desserts',
  'Drinks',
  'Sauces',
  'Other',
  'To Try'
] as const;

export const RecipeFilterToolbar: React.FC<RecipeFilterToolbarProps> = ({
  filters,
  pagination,
  loading = false,
  selectedCount = 0,
  onSearchChange,
  onCategoryChange,
  onFavoritesToggle,
  onShowOwnRecipesOnlyToggle,
  onLimitChange,
  onNextPage,
  onPreviousPage,
  onExportSelected,
  onExportAll,
  onImport,
  isExporting = false,
  showActions = false,
  showStats: _showStats = true,
  variant: _variant = 'home',
}) => {
  const totalPages = pagination.limit > 0 && pagination.totalCount > 0
    ? Math.ceil(pagination.totalCount / pagination.limit)
    : 1;

  const hasActiveFilters = filters.category !== 'all' || filters.showFavoritesOnly || filters.showOwnRecipesOnly || filters.searchQuery.length > 0;

  return (
    <div className="flex flex-col gap-2">
      {/* Toolbar row */}
      <div className="flex items-center gap-2">
        {/* Search moved to top nav (ModernHeader) — see App.tsx */}

        {/* Category */}
        <div className="shrink-0">
          <Select value={filters.category} onValueChange={onCategoryChange}>
            <SelectTrigger className="h-10 w-[140px] rounded-lg bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700 text-sm px-3">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="rounded-lg border-stone-200 dark:border-stone-700">
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="Uncategorized">Uncategorized</SelectItem>
              {CATEGORIES.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Favorites */}
        <button
          type="button"
          onClick={() => onFavoritesToggle(!filters.showFavoritesOnly)}
          className={`shrink-0 h-10 w-10 flex items-center justify-center rounded-lg border transition-colors ${filters.showFavoritesOnly
            ? 'bg-terracotta-50 dark:bg-terracotta-900/20 border-terracotta-200 dark:border-terracotta-800/50 text-terracotta-600 dark:text-terracotta-400'
            : 'bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300'
            }`}
          title="Favorites only"
        >
          <Star className={`h-4 w-4 ${filters.showFavoritesOnly ? 'fill-terracotta-500' : ''}`} />
        </button>

        {/* My Recipes */}
        <button
          type="button"
          onClick={() => onShowOwnRecipesOnlyToggle(!filters.showOwnRecipesOnly)}
          className={`shrink-0 h-10 w-10 flex items-center justify-center rounded-lg border transition-colors ${filters.showOwnRecipesOnly
            ? 'bg-sage-50 dark:bg-sage-900/20 border-sage-200 dark:border-sage-800/50 text-sage-600 dark:text-sage-400'
            : 'bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300'
            }`}
          title="My recipes only"
        >
          <User className="h-4 w-4" />
        </button>

        {/* Spacer - pushes pagination to the right */}
        <div className="flex-1" />

        {/* Import/Export - manage only */}
        {showActions && (
          <>
            <button
              type="button"
              onClick={onImport}
              className="shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 text-stone-500 hover:text-emerald-600 transition-colors"
              title="Import"
            >
              <FileUp className="h-4 w-4" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 text-stone-500 hover:text-blue-600 transition-colors"
                  title="Export"
                >
                  <FileDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-lg">
                <DropdownMenuItem onClick={onExportSelected} disabled={selectedCount === 0} className="py-2">
                  <Download className="h-4 w-4 mr-2 text-blue-500" />
                  Export Selected ({selectedCount})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onExportAll} disabled={isExporting} className="py-2">
                  <Database className="h-4 w-4 mr-2 text-emerald-500" />
                  {isExporting ? 'Exporting...' : 'Export All'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        {/* Pagination */}
        <div className="shrink-0 flex items-center gap-1">
          <div className="hidden sm:flex items-center gap-0.5">
            {[25, 50, 100].map((limit) => (
              <button
                key={limit}
                type="button"
                onClick={() => onLimitChange(limit)}
                disabled={loading}
                className={`h-7 px-2 text-xs font-medium rounded transition-colors disabled:opacity-50 ${pagination.limit === limit
                  ? 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900'
                  : 'text-stone-500 hover:bg-stone-100 dark:text-stone-400 dark:hover:bg-stone-800'
                  }`}
              >
                {limit}
              </button>
            ))}
          </div>
          {pagination.limit !== -1 && totalPages > 0 && (
            <span className="text-xs text-stone-400 dark:text-stone-500 tabular-nums px-1 select-none">
              {pagination.currentPage}/{totalPages}
            </span>
          )}
          <button
            type="button"
            onClick={onPreviousPage}
            disabled={pagination.currentPage === 1 || loading}
            className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onNextPage}
            disabled={pagination.currentPage >= totalPages || loading}
            className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {filters.searchQuery.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 text-xs font-medium">
              <Search className="h-3 w-3" />
              <span className="max-w-[180px] truncate">{filters.searchQuery}</span>
              <button type="button" onClick={() => onSearchChange('')} className="hover:text-stone-900 dark:hover:text-white">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.category !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 text-xs font-medium">
              {filters.category}
              <button type="button" onClick={() => onCategoryChange('all')} className="hover:text-stone-900 dark:hover:text-white">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.showFavoritesOnly && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-terracotta-50 dark:bg-terracotta-900/30 text-terracotta-700 dark:text-terracotta-300 text-xs font-medium">
              <Star className="h-3 w-3 fill-current" />
              Favorites
              <button type="button" onClick={() => onFavoritesToggle(false)} className="hover:text-terracotta-900 dark:hover:text-terracotta-100">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.showOwnRecipesOnly && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sage-50 dark:bg-sage-900/30 text-sage-700 dark:text-sage-300 text-xs font-medium">
              <User className="h-3 w-3" />
              Mine
              <button type="button" onClick={() => onShowOwnRecipesOnlyToggle(false)} className="hover:text-sage-900 dark:hover:text-sage-100">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              onSearchChange('');
              onCategoryChange('all');
              onFavoritesToggle(false);
              onShowOwnRecipesOnlyToggle(false);
            }}
            className="text-xs text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 ml-1"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default RecipeFilterToolbar;
